import TextLink from 'components/Luxkit/TextLink'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import { rem } from 'polished'
import offerPageURL from 'lib/offerPageURL'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import GeoContext from 'contexts/geoContext'
import FormatCurrency from 'components/Common/FormatCurrency'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import { isBedbank } from 'lib/offerTypes'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'
import InlineGroup from 'components/Common/InlineGroup'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import ModalFooter, { ModalFooterActionProps } from 'components/Luxkit/Modal/ModalFooter'
import { stopPropagation } from 'lib/eventHandlers'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import { Trans, useTranslation } from 'react-i18next'
import Group from 'components/utils/Group'

const ModalDescription = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: ${rem(10)};

  & > * {
    padding: ${rem(16)} 0;

    &:last-child {
      padding: ${rem(8)} 0;
    }

    & + * {
      border-top: 1px solid ${props => props.theme.palette.neutral.six};
    }
  }
`

const OptionPanel = styled.div`
  p {
    margin-top: ${rem(4)};
  }
`

const PriceDetailsModalExtraDesc = styled.div`
  width: 100%;
  background-color: ${props => props.theme.palette.primary.lightest};
  padding: ${rem(12)};
  margin-top: ${rem(24)};
`

const SwitchButton = styled(SmallToggleButton)`
  margin-top: ${rem(4)};
`

const ModalWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledLink = styled(TextLink)`
  &.icon-left >:first-child {
    margin-right: ${rem(0)};
  }
`

interface Props {
  offerPage?: boolean;
  offer: App.Offer | App.BedbankOffer | App.OfferSummary | App.BedbankOfferSummary;
  duration: number;
  taxesAndFees: number;
  propertyFees?: number;
  hotelPrice: number;
  flightPrice?: number;
  rooms?: Array<App.Occupants>;
  extraGuestMessage?: string;
  dueAtPropertyMessage?: string;
  propertiesFees?: App.PropertyFeesDetails[];
  label?: 'infoButton' | 'link';
  labelSize?: 'large' | 'small' | 'caption';
  cta: React.ReactNode;
  onCtaAfterClose?: () => void;
}

let onAfterClose = () => {}

function PriceDetailsModal(props: Props) {
  const { offerPage, offer, duration, taxesAndFees, propertyFees, propertiesFees, hotelPrice, flightPrice, rooms, extraGuestMessage, dueAtPropertyMessage, label, labelSize, cta, onCtaAfterClose } = props
  const { t } = useTranslation(['common', 'hotelOfferList'])
  const [showModal, setShowModal] = useState<boolean>(false)
  const onShowModal = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setShowModal(true)
  }, [])
  const onCloseModal = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault()
    setShowModal(false)
  }, [])

  const [expanded, setExpanded] = useState<boolean>(false)
  const expand = () => {
    setExpanded(!expanded)
  }

  useEffect(() => {
    if (!showModal) {
      onAfterClose = () => {}
    }
  }, [showModal])

  const onFinePrint = useCallback(() => {
    onAfterClose = () => {
      window.location.href = '#fine-print'
    }
    onCloseModal()
  }, [onCloseModal])

  const onCTA = useCallback(() => {
    if (onCtaAfterClose) {
      onAfterClose = onCtaAfterClose
    }
    onCloseModal()
  }, [onCloseModal, onCtaAfterClose])

  const { showTaxExclusivePrices } = useContext(GeoContext)
  const priceBeforeTax = (showTaxExclusivePrices ? hotelPrice : hotelPrice - taxesAndFees) + (flightPrice ?? 0)
  const priceAfterTax = (showTaxExclusivePrices ? hotelPrice + taxesAndFees : hotelPrice) + (flightPrice ?? 0)

  const modalFooterSecondaryActionProps = useMemo<ModalFooterActionProps>(() => ({
    children: cta,
    onClick: onCTA,
    as: 'div', // To avoid internal TextButton extra spaces
    kind: 'secondary', // To avoid internal TextButton underline (tertiary button kind has an underline)
  }), [onCTA, cta])

  return (
    <ModalWrapper role="presentation" onClick={stopPropagation}>
      {label === 'link' &&
        <TextLink size={labelSize} onClick={onShowModal}>
          Price details
        </TextLink>
      }
      {label === 'infoButton' &&
        <StyledLink size={labelSize} startIcon={<LineInfoCircleIcon colour="neutral-two" />} onClick={onShowModal}/>
      }

      <ModalBase
        isOpen={showModal}
        size="large"
        onClose={onCloseModal}
        onAfterClose={onAfterClose}
      >
        <ModalHeader title="Price details" onCloseButtonClick={onCloseModal}/>
        <ModalBody>
          <ModalContent>
            <ModalDescription>
              <OptionPanel>
                <InlineGroup horizontal="between">
                  <div>
                    <BodyText variant="medium" weight="bold">
                      {t('night_count', { count: duration, ns: 'common' })} ({t('saleUnit', { ns: 'common', count: rooms?.length ?? 1, context: offer.saleUnit, defaultValue: offer.saleUnit })})
                    </BodyText>
                  </div>
                  <div>
                    <Group direction="horizontal-reverse">
                      <BodyText variant="medium">
                        <FormatCurrency value={priceBeforeTax} format="roundedDollar" />
                      </BodyText>
                    </Group>
                    {duration > 1 && <>
                      <BodyText as="span" variant="medium">
                        <FormatCurrency value={(priceBeforeTax / duration)} format="roundedDollar" />
                      </BodyText>{' '}
                      <Caption as="span" variant="large">{t('average per night', { ns: 'hotelOfferList' })}</Caption>
                    </>}
                  </div>
                </InlineGroup>
              </OptionPanel>
              {(taxesAndFees ?? 0) > 0 && <OptionPanel>
                <InlineGroup horizontal="between">
                  <div>
                    <BodyText variant="medium" weight="bold">
                      {t('Taxes and fees', { ns: 'common' })}
                    </BodyText>
                  </div>
                  <div>
                    <BodyText variant="medium">
                      <FormatCurrency value={taxesAndFees} format="roundedDollar" />
                    </BodyText>
                  </div>
                </InlineGroup>
                {expanded && <BodyText as="p" variant="medium">
                  {t('taxesAndFeesDescription', { ns: 'hotelOfferList' })}
                  {' '}
                  {isBedbank(offer) && <Trans i18nKey="bedbankTaxesDescriptionWithLink" ns="hotelOfferList">
                    For more details, please see the <TextLink weight="regular" size="large" href="https://developer.expediapartnersolutions.com/terms/en" target="_blank">Terms and Conditions</TextLink>.
                  </Trans>}
                  {!isBedbank(offer) &&
                    <Trans i18nKey="taxesDescriptionWithLink" ns="hotelOfferList">
                      Please refer to the {offerPage ? <TextLink weight="regular" onClick={onFinePrint}>‘Fine Print’</TextLink> : <TextLink weight="regular" to={`${offerPageURL(offer)}#fine-print`}>‘Fine Print’ </TextLink>} for any additional costs payable directly to the property.
                    </Trans> }
                </BodyText>}
                <SwitchButton onClick={expand} on={expanded} size="small">
                  {expanded ? t('Read Less', { ns: 'common' }) : t('Learn More', { ns: 'common' })}
                </SwitchButton>
              </OptionPanel>}
              {(propertyFees ?? 0) > 0 && !propertiesFees && <OptionPanel>
                <InlineGroup horizontal="between">
                  <div>
                    <BodyText variant="medium" weight="bold">
                      {t('Due at property', { ns: 'hotelOfferList' })}
                    </BodyText>
                  </div>
                  {!!propertyFees && <div>
                    <BodyText variant="medium">
                      <FormatCurrency value={propertyFees} format="roundedDollar" />
                    </BodyText>
                  </div>
                  }
                </InlineGroup>
              </OptionPanel>}
              {propertiesFees?.map(p => <OptionPanel key={p.propertyName}>
                <InlineGroup horizontal="between">
                  <div>
                    <BodyText variant="medium" weight="bold">
                      Local Tax
                    </BodyText>
                    <Caption colour="neutral-three" variant="medium">
                      {t('Payable at property', { ns: 'hotelOfferList', propertyName: p.propertyName })}
                    </Caption>
                  </div>
                  <div>
                    <BodyText variant="medium">
                      <FormatCurrency value={p.propertyFees} format="roundedDollar" />
                    </BodyText>
                  </div>
                </InlineGroup>
              </OptionPanel>)}
              <OptionPanel>
                <InlineGroup horizontal="between">
                  <div>
                    <BodyText variant="large" weight="bold">
                      {t('Total', { ns: 'common' })}
                    </BodyText>
                  </div>
                  <div>
                    <BodyText variant="large" weight="bold">
                      <FormatCurrency value={priceAfterTax + (propertyFees ?? 0)} format="roundedDollar" />
                    </BodyText>
                  </div>
                </InlineGroup>
                {extraGuestMessage && <PriceDetailsModalExtraDesc>
                  <BodyText variant="large" weight="bold">{t('Extra guest surcharges', { ns: 'hotelOfferList' })}</BodyText>
                  <MarkdownRender content={extraGuestMessage}/>
                </PriceDetailsModalExtraDesc>}
                {dueAtPropertyMessage && <PriceDetailsModalExtraDesc>
                  <BodyText variant="large" weight="bold">{t('Due at property', { ns: 'hotelOfferList' })}</BodyText>
                  <MarkdownRender content={dueAtPropertyMessage}/>
                </PriceDetailsModalExtraDesc>}
              </OptionPanel>
            </ModalDescription>
          </ModalContent>
        </ModalBody>
        <ModalFooter secondaryActionProps={modalFooterSecondaryActionProps} />
      </ModalBase>
    </ModalWrapper>
  )
}

PriceDetailsModal.defaultProps = {
  label: 'link',
  labelSize: 'caption',
}

export default PriceDetailsModal
